import type { CSSProperties } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm } from "@/features/confirm/ConfirmProvider";

// Starts impersonating `userId`. On success it refreshes the auth context and
// navigates to "/", where the role-redirect routes to the impersonated user's
// home (rep → /dashboard, finance → /finance). Stop from the banner in
// ProtectedShell. Admin-only on the server; the button is only rendered for
// admins.
export function ImpersonateButton({
  userId,
  label,
  buttonLabel = "View as",
  style,
}: {
  userId: string;
  label: string;
  buttonLabel?: string;
  style?: CSSProperties;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const impersonate = useMutation({
    mutationFn: () => api.post("/api/admin/impersonate", { user_id: userId }),
    onSuccess: async () => {
      toast.ok(`Now viewing as ${label}. Exit from the banner.`);
      await qc.invalidateQueries({ queryKey: ["me"] });
      navigate({ to: "/" });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't impersonate."),
  });

  return (
    <button
      type="button"
      disabled={impersonate.isPending}
      style={style}
      onClick={async () => {
        const ok = await confirm({
          title: "View as user",
          prompt: `Start viewing the app as ${label}? You can stop from the banner at the top.`,
          confirmLabel: "View as",
        });
        if (ok) impersonate.mutate();
      }}
    >
      {impersonate.isPending ? "Starting…" : buttonLabel}
    </button>
  );
}
